import { ContentFile } from "@scrile/api-provider/dist/api/ContentFilesProvider";
import { FileData } from "@scrile/api-provider/dist/api/FileProvider";
import { VaultFile } from "@scrile/api-provider/dist/api/VaultsProvider";
import { WelcomeMessageFile } from "@scrile/api-provider/dist/api/WelcomeMessagesProvider";
import { getFileMimeType } from "@scrile/tools/dist/lib/FileHelpers";
import { MediaFilesController } from "@/lib/MediaFilesController";
import { getMediaThumbnailUrl } from "@/lib/urlHelpers";
import config from "@/config";
import { AttachmentFile, InputAttachmentFile, PostFileData } from "@/types";

export { getFileMimeType, getNameMimeType } from "@scrile/tools/dist/lib/FileHelpers";

export type FileType = ContentFile | PostFileData | FileData | AttachmentFile | WelcomeMessageFile | VaultFile | File;

export const isFile = (file: FileType): file is File => {
  return file instanceof File;
};

function getMimeType(f: FileType): string {
  if (isFile(f)) {
    return getFileMimeType(f);
  }
  if ("mediaType" in f) {
    return f.mediaType;
  } else if ("type" in f) {
    return f.type;
  } else {
    return f.metadata.contentType || "";
  }
}

export function getStereoFormat(f: FileType): string {
  return "metadata" in f ? f.metadata?.stereoFormat ?? "" : "";
}

export function isVideoFile(f: FileType) {
  return getMimeType(f).startsWith("video");
}

export function isStereoFile(f: FileType) {
  return !!getStereoFormat(f);
}

export function isImageFile(f: FileType) {
  return getMimeType(f).startsWith("image");
}

export function isAudioFile(f: FileType) {
  return getMimeType(f).startsWith("audio");
}

export function isTextFile(f: FileType) {
  return getMimeType(f).startsWith("text");
}

export function isDocumentFile(f: FileType) {
  return getMimeType(f).startsWith("application");
}

export function isMediaFile(f: FileType) {
  return isVideoFile(f) || isImageFile(f);
}

export function isPostFileData(f: ContentFile | PostFileData): f is PostFileData {
  return Reflect.has(f, "uploadProgress");
}

export function getFilesIcon(files: (ContentFile | FileData)[]) {
  const result = files.reduce<{ image: number; video: number; stereo: number; audio: number; files: number }>(
    (out, f) => {
      if (isStereoFile(f)) out.stereo++;
      else if (isVideoFile(f)) out.video++;
      else if (isImageFile(f)) out.image++;
      else if (isAudioFile(f)) out.audio++;
      else out.files++;
      return out;
    },
    { image: 0, video: 0, stereo: 0, audio: 0, files: 0 }
  );
  if (result.stereo > 0 && !result.image && !result.audio && !result.video && !result.files) return "sci-360";
  else if (result.image === 1 && !result.audio && !result.video && !result.files) return "";
  else if (result.files === 1 && !result.audio && !result.video && !result.image) return "";
  else if (result.video === 1 && !result.image && !result.audio && !result.files) return "sci-play";
  else if (result.audio === 1 && !result.image && !result.video && !result.files) return "sci-music-box";
  return "sci-file-multiple";
}

export function getPostAllowedFileTypes() {
  return config.postAllowedFileTypes.reduce((out, item) => {
    type FileTypeKeys = keyof typeof config.fileExtensions;
    const isConfigAcceptType = (type: string): type is FileTypeKeys =>
      Object.keys(config.fileExtensions).includes(type);
    if (isConfigAcceptType(item)) {
      out.push(item);
    } else {
      for (let type in config.fileExtensions) {
        if (isConfigAcceptType(type) && config.fileExtensions[type].includes(item.replace(".", ""))) {
          out.push(item.startsWith(".") ? item : `.${item}`);
        }
      }
    }
    return out;
  }, [] as string[]);
}

export const isAttachmentFile = (file: AttachmentFile | InputAttachmentFile | File): file is AttachmentFile => {
  return "uid" in file;
};
export const isInputAttachmentFile = (file: AttachmentFile | InputAttachmentFile): file is InputAttachmentFile => {
  return "file" in file;
};

export interface BlobMetadata {
  duration: number;
  width: number;
  height: number;
}
export const getAudioVideoMetadata = async (scr: string): Promise<BlobMetadata> => {
  return new Promise(resolve => {
    const video = document.createElement("video");
    video.muted = true;
    const source = document.createElement("source");
    source.src = scr;
    video.preload = "metadata";
    video.appendChild(source);
    video.onloadedmetadata = function() {
      resolve({
        duration: video.duration * 1000,
        width: video.videoWidth,
        height: video.videoHeight,
      });
    };
  });
};
export const getBlobMetadata = async (file: Blob): Promise<BlobMetadata> => {
  const url = URL.createObjectURL(file);
  const data = await getAudioVideoMetadata(url);
  URL.revokeObjectURL(url);
  return data;
};

export const attachmentFileAdapter = (files: VaultFile[], publicAccess = false): AttachmentFile[] => {
  return files.map(file => ({
    src: !isMediaFile(file) ? MediaFilesController.getFileUrl(file.urlPart) : getMediaThumbnailUrl(file, file.urlPart),
    uid: file.uuid,
    type: file.metadata.contentType,
    publicAccess,
    metadata: file.metadata,
  }));
};
export const checkFilesAllowAccept = (files: FileType[], acceptTypes: string[]) => {
  const isTypeRestricted = (contentType: string) => {
    const [type, ..._rest] = contentType.split("/");
    const normalizeType = (contentType: string): string => {
      return contentType === "application" || contentType === "text" ? "document" : contentType;
    };
    const normalizedType = normalizeType(type);
    if (!acceptTypes.includes(normalizedType)) return true;
  };

  return !files.some(file => isTypeRestricted(getMimeType(file)));
};
